import { useMemo } from "react";

import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import { DataSmartTableProps } from "@/Layouts/components/types";
import { BASE_TABLE_PROPS, DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT, ID_GRID_COLDEF } from "@/Washouts/constants";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { HEADER_CLASS_NAME, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { getFormatUTCDate } from "@/Common/helpers/Dates";

import clsx from "clsx";
import TableCustomComponents from "@/DataPages/components/common/PaginationHeaderLoading";
import { GridColumnHeaderParams } from "@mui/x-data-grid-pro";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const maxRows = 10;

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const customProps: DataSmartTableProps["customProps"] = {
	...BASE_TABLE_PROPS,
	tableContainerClassName: clsx("[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled !h-full", {
		"[&_.toolbar-actions]:!absolute [&_.toolbar-actions]:!right-[0px]": true,
	}),
	tableClassName: {
		"functional-table display-select-all-checkbox": true,
		"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
		"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
		"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
		"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:!mr-[10px]": true,
		"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:ml-[4px]": true,
		"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root>.MuiTouchRipple-root]:!invisible": true,
		...TAILWIND_FOR_DATA_SMART_TABLE,
	},
	aggregationFooterClassName: "aggregation-footer regular-footer",
	expandableRows: false,
	hide: false,
};

const columnVisibilityModel = {
	_id: false,
	salesEmployeeId: false,
	financeEmployeeId: false,
	salesManagerId: false,
	sourceLocationId: false,
};

const baseColumnDefinition: Partial<GridColDef> = {
	headerClassName: HEADER_CLASS_NAME,
	renderCell: (params) => <TypographyCell params={params} />,
	renderHeader: (params) => <TypographyHeader params={params} />,
};

const renderHeaderInTwoLines = (part1: string, part2: string, params: GridColumnHeaderParams) => (
	<TypographyHeader params={params}>
		{part1}
		<br />
		{part2}
	</TypographyHeader>
);

const getBaseColumnDefinition = (width?: number) => {
	if (!width) {
		return baseColumnDefinition;
	}

	return {
		...baseColumnDefinition,
		width,
		minWidth: width,
		maxWidth: width,
	};
};

const columns: GridColDef[] = [
	ID_GRID_COLDEF,
	{
		...getBaseColumnDefinition(80),
		field: "prospectId",
		headerName: "Prospect ID",
	},
	{
		...getBaseColumnDefinition(70),
		field: "nameIdentification",
		headerName: "DMS ID #",
	},
	{
		...getBaseColumnDefinition(75),
		field: "dispositionEventId",
		headerName: "Event ID",
		renderHeader: (params) => renderHeaderInTwoLines("Event", "ID", params),
		valueGetter: (value, row) => row.event.id ?? value,
	},
	{
		...getBaseColumnDefinition(120),
		field: "dispositionEventName",
		headerName: "Event Name",
		renderHeader: (params) => renderHeaderInTwoLines("Event", "Name", params),
		valueGetter: (value, row) => row.event.name ?? value,
	},
	{
		...getBaseColumnDefinition(120),
		field: "userIdCompletedBy",
		headerName: "Completed By",
		renderHeader: (params) => renderHeaderInTwoLines("Completed", "By", params),
	},
	{
		...getBaseColumnDefinition(117),
		field: "completedOn",
		headerName: "Completed On",
		type: "dateTime",
		renderHeader: (params) => renderHeaderInTwoLines("Completed", "On", params),
		valueGetter: (value, _row) => new Date(value),
		valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateTime12hPadded),
	},
	{
		...getBaseColumnDefinition(118),
		field: "prospectIdMerged",
		headerName: "Merged Prospect ID",
		renderHeader: (params) => renderHeaderInTwoLines("Merged", "Prospect ID", params),
	},
	{
		...getBaseColumnDefinition(120),
		field: "vinNumber",
		headerName: "VIN",
	},
	{
		...getBaseColumnDefinition(),
		field: "stockType",
		headerName: "Stock Type",
		renderHeader: (params) => renderHeaderInTwoLines("Stock", "Type", params),
	},
	{
		...getBaseColumnDefinition(120),
		field: "primarySalesperson",
		headerName: "Primary Salesperson",
		renderHeader: (params) => renderHeaderInTwoLines("Primary", "Salesperson", params),
	},
	{
		...getBaseColumnDefinition(117),
		field: "appointmentStartDate",
		headerName: "Appt. Data Start Time",
		renderHeader: (params) => renderHeaderInTwoLines("Appt. Data", "Start Time", params),
		valueGetter: (value, row) => {
			if (!row.appointment?.startDate && !value) {
				return null;
			}
			return new Date(row.appointment.startDate ?? value);
		},
		valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateTime12hPadded),
	},
	{
		...getBaseColumnDefinition(117),
		field: "appointmentEndDate",
		headerName: "Appt. Data End Time",
		renderHeader: (params) => renderHeaderInTwoLines("Appt. Data", "End Time", params),
		valueGetter: (value, row) => {
			if (!row.appointment?.endDate && !value) {
				return null;
			}
			return new Date(row.appointment.endDate ?? value);
		},
		valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateTime12hPadded),
	},
];

interface IDispositionsTableProps {
	rows?: any[];
	hasNextPage: boolean;
	isFetching: boolean;
}

export default function DispositionsTable(props: IDispositionsTableProps) {
	const { rows = [], hasNextPage, isFetching } = props;
	const apiRef = useGridApiRef();
	const slotProps = useMemo(
		() => ({
			toolbar: {
				customComponent: (
					<TableCustomComponents
						hasNextPage={hasNextPage}
						isFetching={isFetching}
						text={"Lead Dispositions data is still loading..."}
					/>
				),
				filter: {
					data: rows ?? [],
					hide: false,
					disabled: false,
				},
				search: {
					useQuickFilter: true,
				},
				expand: {
					hide: true,
				},
			},
			noRowsOverlay: {
				title: "No Dispositions Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Dispositions Data",
				className: "!text-montserrat",
			},
		}),
		[rows, hasNextPage, isFetching]
	);

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={columns}
			rows={rows ?? []}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			maxRows={maxRows}
			pinnedColumns={{}}
			getRowClassName={(_params) => ""}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			columnVisibilityModel={columnVisibilityModel}
			slots={slots}
			slotProps={slotProps}
			hideFooter
			customProps={customProps}
			rowHeight={DEFAULT_ROW_HEIGHT}
			autoHeight={false}
		/>
	);
}
